import React, { useState } from 'react';
import axios from 'axios';

// Admin upload for the MT5 symbol-spec export (vix_symbol_specs.json produced by
// mt5/VixSymbolSpecExporter.mq5). Reads the file in the browser and POSTs its JSON
// to the backend ingest endpoint, which upserts into tester_broker_symbol_specs so
// backtests respect real margin/leverage, lot limits, and stop/freeze levels.
function SymbolSpecUploader() {
  const [fileName, setFileName] = useState('');
  const [payload, setPayload] = useState(null);
  const [parseError, setParseError] = useState('');
  const [status, setStatus] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleFile = (event) => {
    setResult(null);
    setStatus('');
    setParseError('');
    setPayload(null);
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(String(reader.result));
        if (!json || !Array.isArray(json.symbols)) {
          throw new Error('File does not look like a VixSymbolSpecExporter export (missing "symbols" array).');
        }
        setPayload(json);
      } catch (err) {
        setParseError(err.message || 'Could not parse JSON file.');
      }
    };
    reader.onerror = () => setParseError('Could not read the selected file.');
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!payload) return;
    setBusy(true);
    setStatus('Uploading…');
    setResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/tester/symbol-specs/import`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(data);
      setStatus('');
    } catch (err) {
      setStatus(err.response?.data?.error || err.message || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overview">
      <div className="overview-header">
        <div>
          <span className="tag">Strategy Tester</span>
          <h1 className="page-title">MT5 Symbol Specs</h1>
          <p className="page-lead">
            Upload the <code>vix_symbol_specs.json</code> exported by the MT5 script so backtests use
            real broker margin (your account leverage), lot limits, and stop/freeze levels.
          </p>
        </div>
      </div>

      <div className="section-card">
        <input type="file" accept="application/json,.json" onChange={handleFile} disabled={busy} />
        {fileName && <p className="muted-text">Selected: {fileName}</p>}
        {parseError && <p style={{ color: '#ff6b6b' }}>{parseError}</p>}

        {payload && !parseError && (
          <div style={{ marginTop: 12 }}>
            <p className="muted-text">
              Broker: <strong>{payload.brokerServer || 'unknown'}</strong> · Leverage 1:
              {payload.accountLeverage || '?'} · {Array.isArray(payload.symbols) ? payload.symbols.length : 0} symbols
              in file · Exported {payload.exportedAt || 'unknown'}
            </p>
            <button className="btn-primary" onClick={handleUpload} disabled={busy}>
              {busy ? 'Importing…' : 'Import specs'}
            </button>
          </div>
        )}

        {status && <p style={{ marginTop: 12 }}>{status}</p>}

        {result && (
          <div style={{ marginTop: 16 }}>
            <h2>Import complete</h2>
            <p className="muted-text">
              Imported <strong>{result.importedCount}</strong> of {result.uploadedSymbolCount} uploaded symbols
              into broker profile <code>{result.brokerProfileId}</code> (leverage 1:{result.accountLeverage}).
              {result.unmappedCount ? ` ${result.unmappedCount} broker symbols were not VixAlgoBuilder symbols and were skipped.` : ''}
            </p>
            <p className="muted-text">
              Mapped symbols: {Array.isArray(result.importedSymbols) ? result.importedSymbols.join(', ') : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SymbolSpecUploader;
